import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { staffList } from "../lib/chatClient";

const STAFF_ROLES = new Set(["admin", "designer", "accountant", "marketing"]);
const POLL_MS = 5000;

// Staff-only nav icon: steady green when no customer is waiting, red heartbeat
// with the waiting count when there are unread live-chat requests.
export function LiveChatNavIcon() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const staff = !!user && STAFF_ROLES.has(user.role);
  const [waiting, setWaiting] = useState(0);

  useEffect(() => {
    if (!staff) return undefined;
    let dead = false;
    const tick = async () => {
      try {
        const data = await staffList("open");
        if (!dead) setWaiting(data.unread || 0);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { dead = true; clearInterval(id); };
  }, [staff]);

  if (!staff) return null;
  const hot = waiting > 0;
  const active = location.pathname.startsWith("/admin/chat");
  return (
    <button
      type="button"
      onClick={() => navigate("/admin/chat")}
      className={`relative inline-flex items-center gap-2 px-3 py-2 rounded-sm text-white transition-colors ${
        hot ? "bg-[#C0392B] hover:bg-[#A93226] animate-heartbeat" : "bg-[#1E8E5A] hover:bg-[#17724A]"
      } ${active ? "ring-2 ring-offset-2 ring-kolt-slate-dark" : ""}`}
      title={hot ? `${waiting} customer${waiting === 1 ? "" : "s"} waiting in live chat` : "Live chat — no one waiting"}
      aria-label={hot ? `Live chat: ${waiting} waiting` : "Live chat: no requests"}
      data-testid={`live-chat-nav-icon-${hot ? "hot" : "idle"}`}
    >
      <MessageCircle className="w-[18px] h-[18px]" />
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold hidden sm:inline">Chat</span>
      {hot && (
        <span
          className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#C0392B] text-[11px] font-black flex items-center justify-center"
          data-testid="live-chat-nav-count"
        >
          {waiting}
        </span>
      )}
    </button>
  );
}
